// frontend/src/pages/admin/SalesTracking.jsx
// ============================================
// SALES TRACKING - Date range picker with clean design
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import './ManagementPages.css';
import * as adminApi from '../../services/admin';

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SalesTracking = () => {
  const PAGE_SIZE = 10;
  const [sales, setSales] = useState([]);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRangeText, setDateRangeText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredSales = paymentMethodFilter === 'all'
    ? sales
    : sales.filter(sale =>
      [sale.paymentMethod, sale.finalPaymentMethod]
        .some(method => method?.toLowerCase() === paymentMethodFilter.toLowerCase())
    );
  const totalPages = Math.max(1, Math.ceil(filteredSales.length / PAGE_SIZE));
  const paginatedSales = filteredSales.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalSales = filteredSales.reduce((total, sale) => total + (sale.amount || 0), 0);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: Math.max(0, currentYear - 2015) + 1 }, (_, index) => currentYear - index);
  const oasisOneSales = filteredSales.filter(sale => sale.location?.toLowerCase() === 'oasis 1').length;
  const oasisTwoSales = filteredSales.filter(sale => sale.location?.toLowerCase() === 'oasis 2').length;
  const cashSales = filteredSales.filter(sale =>
    [sale.paymentMethod, sale.finalPaymentMethod].some(method => method?.toLowerCase() === 'cash')
  ).length;
  const eWalletSales = filteredSales.filter(sale =>
    ['gcash', 'maya', 'gotyme', 'seabank'].includes(sale.paymentMethod?.toLowerCase())
    || ['gcash', 'maya', 'gotyme', 'seabank'].includes(sale.finalPaymentMethod?.toLowerCase())
  ).length;

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      console.log('🔐 Token exists:', !!token);
      
      // Fetch sales between dates
      const data = await adminApi.getSalesByDateRange(startDate, endDate);
      
      console.log('✅ Raw API response:', data);
      let salesList = data.sales || [];
      
      console.log('📊 Sales list before sort:', salesList.length);
      console.log('📊 First sale:', salesList[0]);
      
      // Sort sales by booking number in ascending order
      salesList.sort((a, b) => {
        const aNum = a.bookingNumber === 'N/A' ? Infinity : a.bookingNumber;
        const bNum = b.bookingNumber === 'N/A' ? Infinity : b.bookingNumber;
        return aNum - bNum;
      });
      
      console.log('📊 Sales list after sort:', salesList.length);
      
      setSales(salesList);
      
      // Format date range text for display
      const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setDateRangeText(`${start} - ${end}`);
      
    } catch (error) {
      console.error('❌ Error fetching sales:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, paymentMethodFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setSelectedYear('');
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (!year) return;

    const end = Number(year) === currentYear
      ? new Date()
      : new Date(Number(year), 11, 31);
    setStartDate(`${year}-01-01`);
    setEndDate(formatDateInput(end));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Sales Tracking</h1>
        <p className="page-subtitle">View and analyze sales data</p>
      </div>

      {/* Date Range Picker */}
      <div className="date-range-section">
        <div className="quick-ranges">
          <button className="quick-range-btn" onClick={() => handleQuickRange(1)}>Today</button>
          <button className="quick-range-btn" onClick={() => handleQuickRange(7)}>Last 7 Days</button>
          <button className="quick-range-btn" onClick={() => handleQuickRange(30)}>Last 30 Days</button>
          <button className="quick-range-btn" onClick={() => handleQuickRange(90)}>Last 90 Days</button>
          <button className="quick-range-btn" onClick={() => handleYearChange(String(currentYear))}>This Year</button>
          <div className="date-input-group">
            <label htmlFor="sales-year-filter">Year:</label>
            <select
              id="sales-year-filter"
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="date-input"
            >
              <option value="">Custom range</option>
              {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <div className="date-input-group">
            <label htmlFor="sales-payment-method-filter">Payment Method:</label>
            <select
              id="sales-payment-method-filter"
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="date-input"
            >
              <option value="all">All Payment Methods</option>
              <option value="Cash">Cash</option>
              <option value="GCash">GCash</option>
              <option value="Maya">Maya</option>
              <option value="GoTyme">GoTyme</option>
              <option value="SeaBank">SeaBank</option>
            </select>
          </div>
        </div>
        
        <div className="date-range-picker">
          <div className="date-input-group">
            <label>From:</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => {
                setSelectedYear('');
                setStartDate(e.target.value);
              }}
              max={endDate}
              className="date-input"
            />
          </div>
          <div className="date-separator">→</div>
          <div className="date-input-group">
            <label>To:</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => {
                setSelectedYear('');
                setEndDate(e.target.value);
              }}
              min={startDate}
              max={new Date().toISOString().split('T')[0]}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-title">Total Sales</div>
            <div className="stat-value">{formatCurrency(totalSales)}</div>
            <div className="stat-period">{dateRangeText}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-title">Transactions</div>
            <div className="stat-value">{filteredSales.length}</div>
            <div className="stat-period">Total bookings</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-title">Average Sale</div>
            <div className="stat-value">
              {filteredSales.length > 0 ? formatCurrency(totalSales / filteredSales.length) : formatCurrency(0)}
            </div>
            <div className="stat-period">Per transaction</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏝️</div>
          <div className="stat-content">
            <div className="stat-title">Oasis 1 Sales</div>
            <div className="stat-value">{oasisOneSales}</div>
            <div className="stat-period">Transactions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏝️</div>
          <div className="stat-content">
            <div className="stat-title">Oasis 2 Sales</div>
            <div className="stat-value">{oasisTwoSales}</div>
            <div className="stat-period">Transactions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📱</div>
          <div className="stat-content">
            <div className="stat-title">E-wallet Payments</div>
            <div className="stat-value">{eWalletSales}</div>
            <div className="stat-period">Transactions</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💵</div>
          <div className="stat-content">
            <div className="stat-title">Cash Payments</div>
            <div className="stat-value">{cashSales}</div>
            <div className="stat-period">Transactions</div>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="sales-table-container">
        <div className="table-header">
          <h3>Sales Details</h3>
          {filteredSales.length > 0 && (
            <div className="table-info">Showing {filteredSales.length} transactions</div>
          )}
        </div>
        
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading sales data...</p>
          </div>
        ) : (
          <>
            <div className="table-top-controls">
              <div className="showing-summary">
                Showing {filteredSales.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
                -{Math.min(currentPage * PAGE_SIZE, filteredSales.length)} of {filteredSales.length}
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="page-indicator">{currentPage} / {totalPages}</span>
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Reference Code</th>
                    <th>Location</th>
                    <th>Guest Name</th>
                    <th>Payment Method</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.length === 0 ? (
                    <tr className="no-data-row">
                      <td colSpan="6">
                        <div className="no-data">
                          <i className="fas fa-chart-line"></i>
                          <p>No sales data for this period</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedSales.map((sale, idx) => {
                      const referenceCode = sale.referenceCode || 'N/A';
                      const location = sale.location || 'N/A';
                      const customerName = sale.booking?.customerName || sale.reservation?.guestName || 'N/A';
                      
                      console.log(`🟢 Rendering sale ${idx + 1}:`, { referenceCode, location, customerName, amount: sale.amount });
                      
                      return (
                        <tr key={`${referenceCode}-${idx}`}>
                          <td className="reference-code">{referenceCode}</td>
                          <td className="location">{location}</td>
                          <td className="customer-name">{customerName}</td>
                          <td>
                            {sale.finalPaymentMethod
                              ? `${sale.paymentMethod || 'N/A'} / Final: ${sale.finalPaymentMethod}`
                              : sale.paymentMethod || 'N/A'}
                          </td>
                          <td className="amount">{formatCurrency(sale.amount || 0)}</td>
                          <td className="date">{sale.date ? new Date(sale.date).toLocaleDateString() : 'N/A'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-bottom-controls">
              <div className="showing-summary">
                Showing {filteredSales.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
                -{Math.min(currentPage * PAGE_SIZE, filteredSales.length)} of {filteredSales.length}
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="page-indicator">{currentPage} / {totalPages}</span>
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SalesTracking;